import { useState } from "react";
import { CheckCircle2, Info, MessageCircle, TriangleAlert } from "lucide-react";
import axios from "axios";
import { API } from "@/config";
import { getLang, t } from "@/lib/i18n";

const today = () => {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};
const EMPTY = { name: "", phone: "", date: "", people: "", message: "", website: "" };

function validate(v) {
  const e = {};
  if (v.name.trim().length < 3 || !v.name.trim().includes(" ")) e.name = t("form.errors.name");
  const d = v.phone.replace(/\D/g, "");
  if (d.length < 10 || d.length > 13) e.phone = t("form.errors.phone");
  if (!v.date) e.date = t("form.errors.date");
  else if (v.date < today()) e.date = t("form.errors.datePast");
  const n = Number(v.people);
  if (!v.people) e.people = t("form.errors.people");
  else if (!Number.isInteger(n) || n < 1 || n > 1000) e.people = t("form.errors.peopleRange");
  if (v.message.length > 500) e.message = t("form.errors.message");
  return e;
}

const trDate = (iso) => iso.split("-").reverse().join(".");
const waText = (v) =>
  [t("form.waIntro"), `${t("form.name")}: ${v.name.trim()}`, `${t("form.phone")}: ${v.phone.trim()}`, `${t("form.date")}: ${trDate(v.date)}`, `${t("form.people")}: ${v.people}`, v.message.trim() && `${t("form.message")}: ${v.message.trim()}`]
    .filter(Boolean)
    .join("\n");

const inputCls = "w-full min-h-[48px] rounded-xl border bg-white px-4 py-3 text-base text-ink placeholder:text-stone-400 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-honey";

function Field({ id, label, error, optional, children }) {
  return (
    <div className="space-y-2">
      <label htmlFor={id} className="block text-sm font-semibold text-wood">
        {label} {optional && <span className="font-normal text-ink-muted">({t("form.optional")})</span>}
      </label>
      {children}
      {error && <p id={`${id}-error`} className="text-sm text-[#B42318]" data-testid={`${id}-error`}>{error}</p>}
    </div>
  );
}

function Notice({ kind, children, testId }) {
  const styles = { success: "bg-[#ECFDF3] text-[#05603A]", error: "bg-[#FEF3F2] text-[#B42318]", info: "bg-[#FEF3C7] text-[#78350F]" };
  const Icon = kind === "success" ? CheckCircle2 : kind === "error" ? TriangleAlert : Info;
  return (
    <p role={kind === "error" ? "alert" : "status"} className={`flex items-start gap-2 rounded-xl px-4 py-3 text-sm font-medium ${styles[kind]}`} data-testid={testId}>
      <Icon className="h-4 w-4 mt-0.5 shrink-0" aria-hidden="true" />
      {children}
    </p>
  );
}

export default function GroupForm({ whatsapp }) {
  const [v, setV] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [attempted, setAttempted] = useState(false);
  const [status, setStatus] = useState({ state: "idle", message: "" });

  const set = (k) => (e) => {
    const next = { ...v, [k]: e.target.value };
    setV(next);
    if (attempted) setErrors(validate(next));
  };
  const a11y = (k) => ({ id: `group-${k}`, "aria-invalid": !!errors[k], "aria-describedby": errors[k] ? `group-${k}-error` : undefined, "data-testid": `group-form-${k}-input`, className: `${inputCls} ${errors[k] ? "border-[#B42318]" : "border-line"}` });

  const check = () => {
    const errs = validate(v);
    setErrors(errs);
    setAttempted(true);
    const first = Object.keys(errs)[0];
    if (first) document.getElementById(`group-${first}`)?.focus();
    return !first;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!check()) return;
    setStatus({ state: "sending", message: "" });
    try {
      await axios.post(`${API}/group-requests`, { ...v, lang: getLang() });
      setStatus({ state: "success", message: t("form.success") });
      setV(EMPTY);
      setAttempted(false);
    } catch (err) {
      const res = err.response;
      if (res?.status === 422 && res.data?.detail?.fields) {
        const local = validate(v);
        setErrors(Object.fromEntries(Object.keys(res.data.detail.fields).map((k) => [k, local[k] || t("form.errorFields")])));
      }
      const msg = res?.status === 429 ? t("form.errorRate") : res?.status === 422 ? t("form.errorFields") : t("form.errorGeneric");
      setStatus({ state: "error", message: msg });
    }
  };

  const onWhatsApp = () => {
    if (!check()) return;
    window.open(`${whatsapp.href}?text=${encodeURIComponent(waText(v))}`, "_blank", "noopener,noreferrer");
  };

  return (
    <form noValidate onSubmit={onSubmit} className="rounded-3xl bg-white border border-line p-6 sm:p-10 space-y-6" data-testid="group-request-form" aria-labelledby="group-form-title">
      <div className="space-y-3">
        <h2 id="group-form-title" className="text-3xl sm:text-4xl font-semibold text-forest">{t("form.title")}</h2>
        <p className="text-ink-muted">{t("form.text")}</p>
      </div>
      {status.state === "success" && <Notice kind="success" testId="group-form-success">{status.message}</Notice>}
      <div className="grid gap-5 sm:grid-cols-2">
        <Field id="group-name" label={t("form.name")} error={errors.name}><input type="text" autoComplete="name" value={v.name} onChange={set("name")} {...a11y("name")} /></Field>
        <Field id="group-phone" label={t("form.phone")} error={errors.phone}><input type="tel" autoComplete="tel" inputMode="tel" value={v.phone} onChange={set("phone")} {...a11y("phone")} /></Field>
        <Field id="group-date" label={t("form.date")} error={errors.date}><input type="date" min={today()} value={v.date} onChange={set("date")} {...a11y("date")} /></Field>
        <Field id="group-people" label={t("form.people")} error={errors.people}><input type="number" inputMode="numeric" min="1" max="1000" value={v.people} onChange={set("people")} {...a11y("people")} /></Field>
      </div>
      <Field id="group-message" label={t("form.message")} error={errors.message} optional>
        <textarea rows={4} value={v.message} onChange={set("message")} {...a11y("message")} />
      </Field>
      <div className="absolute -left-[9999px] h-px w-px overflow-hidden" aria-hidden="true">
        <label htmlFor="group-website">Web sitesi</label>
        <input id="group-website" type="text" tabIndex={-1} autoComplete="off" value={v.website} onChange={set("website")} data-testid="group-form-honeypot" />
      </div>
      <p className="text-sm text-ink-muted" data-testid="group-form-disclaimer">{t("form.disclaimer")}</p>
      {status.state === "error" && <Notice kind="error" testId="group-form-error">{status.message}</Notice>}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <button type="submit" disabled={status.state === "sending"} className="inline-flex items-center justify-center min-h-[52px] px-8 rounded-full bg-forest text-cream font-semibold hover:bg-forest-dark transition-colors disabled:opacity-60" data-testid="group-request-submit-button">
          {status.state === "sending" ? t("form.sending") : t("form.submit")}
        </button>
        {whatsapp && (
          <button type="button" onClick={onWhatsApp} className="inline-flex items-center justify-center gap-2 min-h-[52px] px-7 rounded-full border border-forest/30 text-forest font-semibold hover:bg-forest/[0.05] transition-colors" data-testid="group-request-whatsapp-button">
            <MessageCircle className="h-4 w-4" aria-hidden="true" /> {t("form.whatsapp")}
          </button>
        )}
      </div>
    </form>
  );
}
